package chand.wordvoyage

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.viewmodel.compose.viewModel
import chand.wordvoyage.config.OpenAIConfig
import chand.wordvoyage.data.DetailInfo
import chand.wordvoyage.platform.createFirestoreRepository
import chand.wordvoyage.platform.rememberTextToSpeech
import chand.wordvoyage.repository.OpenAIRepository
import wordvoyage.shared.generated.resources.Res
import wordvoyage.shared.generated.resources.ic_volume_up
import wordvoyage.shared.generated.resources.ic_book
import wordvoyage.shared.generated.resources.ic_usage
import wordvoyage.shared.generated.resources.ic_examples
import chand.wordvoyage.ui.FancyDetailCard
import chand.wordvoyage.ui.FancyLinkCard
import chand.wordvoyage.ui.SectionColorExamples
import chand.wordvoyage.ui.SectionColorMeaning
import chand.wordvoyage.ui.SectionColorPronunciation
import chand.wordvoyage.ui.SectionColorUsage
import chand.wordvoyage.ui.WordHeroBanner
import chand.wordvoyage.ui.WordVoyageTopBar
import chand.wordvoyage.ui.AdBanner
import chand.wordvoyage.viewmodel.DetailInfoState
import chand.wordvoyage.viewmodel.SaveState
import chand.wordvoyage.viewmodel.WordDetailViewModel

@Composable
fun WordDetail(
    word: String,
    userId: String,
    onClose: () -> Unit
) {
    val repository = remember { OpenAIRepository(OpenAIConfig.API_KEY) }
    val firestoreRepository = remember { createFirestoreRepository() }
    val detailViewModel: WordDetailViewModel = viewModel {
        WordDetailViewModel(repository, firestoreRepository, userId)
    }
    val detailInfoState by detailViewModel.detailInfoState.collectAsState()
    val saveState by detailViewModel.saveState.collectAsState()

    LaunchedEffect(word) { detailViewModel.fetchDetailInfo(word) }

    Scaffold(
        topBar = { WordVoyageTopBar(title = "", onBack = onClose) },
        bottomBar = { AdBanner() },
        containerColor = MaterialTheme.colorScheme.background
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
                .verticalScroll(rememberScrollState())
        ) {
            // ── Hero banner ──────────────────────────────────────────────
            val subtitle = when (saveState) {
                is SaveState.Saving -> "Saving to history..."
                is SaveState.Saved  -> "✓ Saved to your history"
                is SaveState.Error  -> "⚠ Could not save"
                else -> ""
            }
            WordHeroBanner(
                word = word,
                subtitle = subtitle,
                accentColor = SectionColorMeaning
            )

            // ── Content ──────────────────────────────────────────────────
            Column(
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 20.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                when (val state = detailInfoState) {
                    is DetailInfoState.Idle, is DetailInfoState.Loading -> {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(32.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                            Text(
                                text = "Fetching details...",
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                    is DetailInfoState.Success -> {
                        val tts = rememberTextToSpeech()
                        val uriHandler = LocalUriHandler.current
                        val info = state.detailInfo

                        // Pronunciation card
                        if (info.howToPronounce.isNotBlank()) {
                            FancyDetailCard(
                                title = "How to Pronounce",
                                icon = Res.drawable.ic_volume_up,
                                accentColor = SectionColorPronunciation,
                                onSpeak = { tts.speak(word) }
                            ) {
                                Text(
                                    text = info.howToPronounce,
                                    style = MaterialTheme.typography.bodyLarge,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }
                        }

                        // Meaning card
                        FancyDetailCard(
                            title = "Meaning",
                            icon = Res.drawable.ic_book,
                            accentColor = SectionColorMeaning
                        ) {
                            Text(
                                text = info.meaning,
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurface
                            )
                        }

                        // Usage card
                        if (info.wordUsage.isNotBlank()) {
                            FancyDetailCard(
                                title = "How to Use",
                                icon = Res.drawable.ic_usage,
                                accentColor = SectionColorUsage
                            ) {
                                Text(
                                    text = info.wordUsage,
                                    style = MaterialTheme.typography.bodyLarge,
                                    color = MaterialTheme.colorScheme.onSurface
                                )
                            }
                        }

                        // Examples card
                        if (info.examplesOfUsingWord.isNotEmpty()) {
                            FancyDetailCard(
                                title = "Examples",
                                icon = Res.drawable.ic_examples,
                                accentColor = SectionColorExamples
                            ) {
                                Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                                    info.examplesOfUsingWord.forEachIndexed { index, example ->
                                        Row(modifier = Modifier.fillMaxWidth()) {
                                            Text(
                                                text = "${index + 1}.",
                                                style = MaterialTheme.typography.bodyLarge,
                                                fontWeight = FontWeight.Bold,
                                                color = SectionColorExamples,
                                                modifier = Modifier.padding(end = 8.dp)
                                            )
                                            Text(
                                                text = example,
                                                style = MaterialTheme.typography.bodyLarge,
                                                color = MaterialTheme.colorScheme.onSurface,
                                                modifier = Modifier.weight(1f)
                                            )
                                        }
                                    }
                                }
                            }
                        }

                        // Learn More card
                        if (info.websiteLinkToStillMoreInfo.isNotBlank()) {
                            FancyLinkCard(url = info.websiteLinkToStillMoreInfo) {
                                try { uriHandler.openUri(info.websiteLinkToStillMoreInfo) }
                                catch (e: Exception) { println("URL error: ${e.message}") }
                            }
                        }
                    }
                    is DetailInfoState.Error -> {
                        Column(
                            modifier = Modifier.fillMaxWidth().padding(16.dp),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Text(
                                text = "⚠ ${state.message}",
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.error
                            )
                            Button(onClick = { detailViewModel.fetchDetailInfo(word) }) {
                                Text("Retry")
                            }
                        }
                    }
                }
                Spacer(modifier = Modifier.height(16.dp))
            }
        }
    }
}
